/** 开票单状态 */
export enum BillingBillStatus {
  PENDING = 'PENDING',
  CONFIRMING = 'CONFIRMING',
  CONFIRMED = 'CONFIRMED'
}

/** 开票行状态 */
export enum BillingLineStatus {
  /** 未打包：尚未发起打包开票 */
  UNPACKED = 'UNPACKED',
  /** 打包中：已提交打包申请，超 2h 无有效结果转开票失败 */
  PACKING = 'PACKING',
  /** 已打包：SAP 回传 billing_status=已打包，允许取消打包 */
  PACKED = 'PACKED',
  /** 已上传：SAP 回传已开票，等待大象平台回传发票文件 */
  UPLOADED = 'UPLOADED',
  /** 开票失败：SAP 失败 / 超 2h 无有效结果 / 主数据包号无效 / 状态查询超时 */
  INVOICE_FAILED = 'INVOICE_FAILED',
  /** 已取消：ZFM_NCRM_048 返回 cancel_status=S 或 HANA 拉取取消成功 */
  CANCELLED = 'CANCELLED',
  /** 已开票：大象平台回传发票文件 */
  INVOICED = 'INVOICED'
}

/** 开票类型 */
export enum BillingType {
  VAT_SPECIAL = 'ZF1',
  VAT_NORMAL = 'ZF2',
  VAT_ELECTRONIC = 'ZF3',
  RED_INVOICE = 'S1'
}

/** 审批状态 */
export enum ApprovalStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  DIRECT_EFFECTIVE = 'DIRECT_EFFECTIVE'
}

/** 发票文件状态 */
export enum InvoiceFileStatus {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  DOWNLOAD_FAILED = 'DOWNLOAD_FAILED'
}

/** 开票单操作类型 */
export enum BillingBillOperationType {
  CONFIRM = 'CONFIRM',
  EDIT = 'EDIT',
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  DOWNLOAD = 'DOWNLOAD',
  SPECIFY = 'SPECIFY',
  CANCEL_PACK = 'CANCEL_PACK',
  REFRESH = 'REFRESH'
}

/** 来源单据类型 */
export enum SourceDocType {
  INTERNAL_TMS = 'INTERNAL_TMS',
  EXTERNAL_ORDER = 'EXTERNAL_ORDER',
  PROPS_MATERIAL = 'PROPS_MATERIAL',
  FREIGHT_VOUCHER = 'FREIGHT_VOUCHER',
  DIFF_ADJUST_INC = 'DIFF_ADJUST_INC',
  AB_DIFF_ADJUST = 'AB_DIFF_ADJUST',
  AFTER_SALE = 'AFTER_SALE',
  EXTERNAL_AFTER_SALE = 'EXTERNAL_AFTER_SALE',
  DIFF_ADJUST_DEC = 'DIFF_ADJUST_DEC'
}

/** 开票单类别 */
export enum BillCategory {
  /** 常规开票：月度对账归集链路生成，参与自动开票 */
  NORMAL = 'NORMAL',
  /** 独立开票：针对已出库但未进入常规归集链路的80单手工发起，不参与自动开票 */
  INDEPENDENT = 'INDEPENDENT'
}

/** SAP 打包开票状态（90单维度，由仓外的90单同步链路写入，CRM 侧只读） */
export enum SapPackStatus {
  /** 未发起打包开票：仅此状态允许发起独立开票 */
  NOT_PACKED = 'NOT_PACKED',
  /** 已发起打包开票 */
  PACKED = 'PACKED',
  /** 已开票 */
  INVOICED = 'INVOICED',
  /** 已取消 */
  CANCELLED = 'CANCELLED'
}

/** 80 单出库状态（后端 OutStockStatusEnum 取值） */
export enum OutStockStatus {
  NO_OUT = 'NO_OUT',
  PART_OUT = 'PART_OUT',
  OUTED = 'OUTED'
}

/** 开票单主表 */
export interface BillingBill {
  id: number;
  code: string;
  name: string;
  distributorId: number;
  distributorCode: string;
  distributorErpCode: string;
  distributorName: string;
  salesOrgId: number;
  salesOrgCode: string;
  salesOrgName: string;
  totalAmount: number;
  invoicedAmount: number;
  remainAmount: number;
  status: BillingBillStatus;
  /** 开票单类别：常规开票 / 独立开票 */
  billCategory?: BillCategory;
  accountStartDate: string;
  accountEndDate: string;
  billDay: number;
  billingType: BillingType;
  invoiceInfoId: number;
  autoInvoice: boolean;
  specifiedInvoiceContent: string;
  /** 使用默认开票信息时手工填写的寄送地址 */
  deliveryAddress: string;
  remark: string;
  createUserName: string;
  writeUserName: string;
  createDate: string;
  writeDate: string;
}

/** 开票单关联单据行 */
export interface BillingBillLine {
  id: number;
  billingBillId: number;
  sourceDocType: SourceDocType;
  sourceDocCode: string;
  tmsOrderCode: string;
  tmsOrderId: number;
  order90Code: string;
  relatedDocType: string;
  relatedDocCode: string;
  bizTime: string;
  salesOrgName: string;
  salesOffice: string;
  distributionChannel: string;
  distributionChannelCode: string;
  taxInclusiveAmount: number;
  billingNetAmount: number;
  billingTaxAmount: number;
  profitCenter: string;
  companyCode: string;
  payer: string;
  lineAmount: number;
  status: BillingLineStatus;
  dataPackageNo: string;
  mainPackageNo: string;
  vatInvoiceNo: string;
  invoiceNo: string;
  deliveryOrderCode: string;
  invoiceFileUrl: string;
  invoiceFileStatus: InvoiceFileStatus;
  errorMsg: string;
  packTime: string;
}

/** 开票信息 */
export interface DeliInvoiceInfo {
  id: number;
  code: string;
  distributorId: number;
  distributorCode: string;
  distributorErpCode: string;
  salesOrgCode: string;
  isDefault: boolean;
  invoiceTitle: string;
  taxNo: string;
  bankName: string;
  bankAccount: string;
  phone: string;
  address: string;
  deliveryAddress: string;
  /** 经销商档案默认开票类型（瞬态，仅档案组装对象返回：customerInvoiceInfo / queryIndependentDefaultInvoiceInfo） */
  defaultBillingType?: string;
  businessLicenseFile: string;
  remark: string;
  approvalStatus: ApprovalStatus;
  submittedBy: string;
  approvedBy: string;
  rejectReason?: string;
  enabled: boolean;
  createDate: string;
  writeDate: string;
}

/** 自动开票配置 */
export interface AutoInvoiceConfig {
  id: number;
  distributorId: number;
  distributorCode: string;
  distributorErpCode: string;
  salesOrgCode: string;
  autoInvoiceEnabled: boolean;
  billingType: BillingType;
  noticeInvoiceNo: string;
  remark: string;
  createDate: string;
  writeDate: string;
}

/** 开票单操作日志 */
export interface BillingBillOperationLog {
  id: number;
  billingBillId: number;
  billingBillCode: string;
  operationType: BillingBillOperationType;
  operatorName: string;
  detail: string;
  createDate: string;
}

/** 指定开票内容明细项 */
export interface SpecifiedInvoiceContentItem {
  lineItemNo?: number;
  specifiedInvoiceContent: string;
  quantity: number;
  amount: number;
  taxAmount: number;
  taxRate: number;
  unit: string;
  specModel: string;
  taxCode: string;
  taxCategoryName: string;
}

/** 分页查询参数 */
export interface PageQueryParams {
  currentPage?: number;
  size?: number;
  rsql?: string;
  queryData?: Record<string, any>;
}

/** 分页结果 */
export interface PageResult<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
}

/** 导入校验结果 */
export interface ImportValidateResult {
  totalRows: number;
  successRows: number;
  failRows: number;
  groupCount: number;
  failReasons: Array<{ row: number; reason: string }>;
  groups: ImportGroup[];
}

/** 导入分组 */
export interface ImportGroup {
  invoiceInfoCode: string;
  remark: string;
  order80Count: number;
  order90Count: number;
  amountTotal: number;
  taxAmountTotal: number;
  items: SpecifiedInvoiceContentItem[];
}

// ==================== 独立开票 ====================

/** 独立开票经销商下拉选项（后端 IndependentBillingDistributor 瞬态模型）。
 *  创建向导第一步按客户 ERP 编码或经销商名称检索，选定后以 distributorCode 驱动销售机构与开票信息下拉。 */
export interface IndependentBillingDistributor {
  id?: number;
  distributorCode: string;
  distributorName: string;
  distributorErpCode: string;
}

/** 独立开票销售机构下拉选项（后端 IndependentBillingSalesOrg 瞬态模型） */
export interface IndependentBillingSalesOrg {
  id?: number;
  salesOrgId: number;
  salesOrgCode: string;
  salesOrgName: string;
  /** 是否为该经销商的默认销售机构，后端已按此排在最前 */
  isDefault: boolean;
}

/** 独立开票候选行（后端 IndependentBillingCandidate 瞬态模型）。
 *  一行 = 一笔90单（SAP VBRP 行项）；不满足准入校验的行已在后端数据源上过滤，不进入返回列表。 */
export interface IndependentBillingCandidate {
  id?: number;
  /** 90单数据ID（SapBilling90Order 主键），提交时作为 candidateIds 回传 */
  candidateId: number;
  order90Code: string;
  tmsOrderCode: string;
  bizTime: string;
  taxInclusiveAmount: number;
  billingNetAmount: number;
  billingTaxAmount: number;
  profitCenter: string;
  companyCode: string;
  invoiceType: string;
  distributorErpCode: string;
  salesOrgCode: string;
  salesOrgName: string;
  /** 80单出库状态，仅 OUTED 允许发起独立开票 */
  outStockStatus: string;
  distributionChannel: string;
  distributionChannelCode: string;
  sourceDocType: SourceDocType;
  sourceDocCode: string;
  packSyncSuccess: boolean;
  packSyncTime: string;
  sapPackStatus: SapPackStatus;
  /** 已被其他开票链路占用时回显的开票行状态 */
  lineStatus: BillingLineStatus;
  dataPackageNo: string;
  invoiceFileUrl: string;
}

/** 独立开票候选查询条件（后端 IndependentBillingCandidateFilter 瞬态模型）。
 *  后端类名不得以 Query 结尾：Pamirs 会为模型 IndependentBillingCandidate 自动生成
 *  GQL 根类型 type IndependentBillingCandidateQuery，查询条件模型若同名会触发启动期
 *  SchemaParser 的 "tried to redefine existing type"。
 *  80/90 单号与数据包号支持粘贴多值批量检索。 */
export interface IndependentBillingCandidateFilter {
  distributorCode?: string;
  distributorErpCode?: string;
  /** 经销商下拉检索关键词：客户 ERP 编码精准匹配，或经销商编码/名称模糊匹配 */
  keyword?: string;
  salesOrgCode?: string;
  distributionChannelCode?: string;
  tmsOrderCodes?: string[];
  order90Codes?: string[];
  dataPackageNos?: string[];
  lineStatus?: BillingLineStatus;
}

/** 独立开票提交入参（后端 IndependentBillingCreateParam 瞬态模型） */
export interface IndependentBillingCreateParam {
  distributorId?: number;
  distributorCode: string;
  salesOrgCode: string;
  /** 为空时后端使用经销商客户信息中的默认开票信息 */
  invoiceInfoId?: number;
  /** 开票类型，传后端枚举名（如 VAT_SPECIAL） */
  billingType?: string;
  /** 红字发票通知单号，红票必填 */
  noticeInvoiceNo?: string;
  deliveryAddress?: string;
  remark?: string;
  /** 勾选的90单数据ID列表 */
  candidateIds: number[];
  /** 指定开票内容（JSON 字符串），普通开票传 null；仅本次打包使用，不落库 */
  specifiedInvoiceContent?: string | null;
}

